import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import OtpInput from 'react-otp-input';
import Input from '@/components/lib/Input';

export type OTPInputRef = { reset: () => void };

interface OTPInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  allowAlphaNumeric?: boolean;
  prefill?: string;
  onChange?: (value: string) => void;
}

const OTPInput = forwardRef<OTPInputRef, OTPInputProps>(
  ({ length = 6, onComplete, allowAlphaNumeric = false, prefill, onChange }, ref) => {
    const [otp, setOtp] = useState(prefill?.slice(0, length).toUpperCase() ?? '');
    const inputRefs = Array.from({ length }, () => useRef<HTMLInputElement | null>(null));
    const prevCompletedRef = useRef<string>('');

    useEffect(() => {
      if (otp.length === length && otp !== prevCompletedRef.current) {
        prevCompletedRef.current = otp;
        onComplete?.(otp);
      }
      if (otp.length < length) {
        prevCompletedRef.current = '';
      }
    }, [otp, length, onComplete]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        setOtp('');
        inputRefs[0].current?.focus();
      },
    }));

    useEffect(() => {
      if (prefill) {
        setOtp(prefill.slice(0, length).toUpperCase());
        inputRefs[inputRefs.length - 1]?.current?.focus();
      }
    }, [prefill, length]);

    const handleChange = (value: string) => {
      const newValue = allowAlphaNumeric ? value.toUpperCase() : value;
      setOtp(newValue);
      onChange?.(newValue);
    };

    return (
      <OtpInput
        shouldAutoFocus
        value={otp}
        onChange={handleChange}
        numInputs={length}
        inputType={allowAlphaNumeric ? 'text' : 'number'}
        inputStyle={{ width: '48px' }}
        renderInput={(props, index) => (
          <Input
            {...props}
            inline
            rounded="2xl"
            pattern={allowAlphaNumeric ? '[A-Z0-9]*' : '[0-9]*'}
            ref={(inputRef) => {
              props.ref(inputRef);
              inputRefs[index].current = inputRef;
            }}
          />
        )}
        renderSeparator={<div style={{ width: '8px' }} />}
      />
    );
  },
);

// Add display name to the component
OTPInput.displayName = 'OTPInput';

export default OTPInput;
